/** Models store — the installed-model list and which one is active. */
import { create } from "zustand";
import type { ModelInfo } from "@/types";
import { fetchInstalledModels } from "@/services/ai";
import { logger } from "@/services/logger";
import { useSettingsStore } from "./settingsStore";

const log = logger.scope("models");

interface ModelsState {
  models: ModelInfo[];
  activeModelId: string | null;
  status: "idle" | "loading" | "error";
  error: string | null;

  refresh: () => Promise<void>;
  setActiveModel: (id: string) => void;
}

export const useModelsStore = create<ModelsState>()((set) => ({
  models: [],
  activeModelId: useSettingsStore.getState().defaultModelId,
  status: "idle",
  error: null,

  refresh: async () => {
    set({ status: "loading", error: null });
    try {
      const baseUrl = useSettingsStore.getState().ollamaHost;
      const models = await fetchInstalledModels(baseUrl);
      set((s) => ({
        models,
        status: "idle",
        activeModelId:
          s.activeModelId && models.some((m) => m.id === s.activeModelId)
            ? s.activeModelId
            : (models[0]?.id ?? null),
      }));
    } catch (err) {
      log.error("Failed to list models", err);
      set({
        status: "error",
        error: err instanceof Error ? err.message : "Failed to list models.",
      });
    }
  },

  setActiveModel: (id) => {
    set({ activeModelId: id });
    useSettingsStore.getState().set("defaultModelId", id);
  },
}));
